import { Admin, Resource, CustomRoutes, ResourceContextProvider } from 'react-admin';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Box, Stack, Typography, TextField, Button } from '@mui/material';
import { RostaLayout } from '@/layouts/RostaLayout';
import { PublicLayout } from '@/layouts/PublicLayout';
import { apiDataProvider } from '@/providers/apiDataProvider';
import { theme } from '@/theme';
import { AuthProvider, useAuth } from '@/hooks/auth';
import { Dashboard } from '@/components/Dashboard';
import { TaskList, TaskShow } from '@/components/task/TaskList';
import { LandingPage } from '@/pages/LandingPage';
import { SignUpPage } from '@/pages/SignUpPage';
import { AboutPage } from '@/pages/AboutPage';
import { PricingPage } from '@/pages/PricingPage';
import { VerifyEmailPage } from '@/pages/VerifyEmailPage';
import {
  EmployeeList, EmployeeShow, EmployeeCreate, EmployeeEdit,
  TrainingList, TrainingShow, TrainingCreate, TrainingEdit,
  TrainingProviderList, TrainingProviderShow, TrainingProviderCreate, TrainingProviderEdit,
  CourseList, CourseShow, CourseCreate, CourseEdit,
  DisciplinaryList, DisciplinaryShow, DisciplinaryCreate, DisciplinaryEdit,
  PipList, PipShow, PipCreate, PipEdit,
  EEProfileList, EEProfileShow, EEProfileCreate, EEProfileEdit,
  TerminationList, TerminationShow, TerminationCreate, TerminationEdit,
  InvoiceList, InvoiceShow, InvoiceCreate, InvoiceEdit, ReceiptList, ReceiptShow, ReceiptCreate, ReceiptEdit,
  CertificationList, CertificationShow, CertificationCreate, CertificationEdit,
  IncidentList, IncidentShow, IncidentCreate, IncidentEdit,
  RepresentativeList, RepresentativeShow, RepresentativeCreate, RepresentativeEdit,
  AuditList, AuditShow, AuditCreate, AuditEdit,
  ComplianceCalendarList, ComplianceCalendarShow, ComplianceCalendarCreate, ComplianceCalendarEdit,
  DSARRequestList, DSARRequestShow, DSARRequestCreate, DSARRequestEdit,
  WspAtrDashboard, ContractList, ContractShow, ContractCreate, ContractEdit,
  OnboardingProcessList, OnboardingProcessShow, OnboardingProcessCreate, OnboardingProcessEdit,
  ContractAlertList, ContractAlertShow, ContractAlertCreate, ContractAlertEdit,
  StatutoryDocumentList, StatutoryDocumentShow, StatutoryDocumentCreate, StatutoryDocumentEdit,
  EmploymentContractList, EmploymentContractShow, EmploymentContractCreate, EmploymentContractEdit,
  DepartmentList, DepartmentShow, DepartmentCreate, DepartmentEdit,
  RoleTemplateList, RoleTemplateShow, RoleTemplateCreate, RoleTemplateEdit,
  UserList, UserShow, UserCreate, UserEdit,
  RoleList, RoleShow, RoleCreate, RoleEdit,
  NotificationList, NotificationShow, NotificationCreate, NotificationEdit,
  AccountList, AccountShow, AccountCreate, AccountEdit,
  CheckpointList, CheckpointShow, CheckpointCreate,
  WorkflowList, WorkflowShow, WorkflowCreate, WorkflowEdit,
  FileListShow, FileShowPage, FileCreatePage, FileEditPage,
  OnboardingDashboard, UserDashboard,
  TESDashboard, TESAssignmentList, TESAssignmentShow, TESVendorList, TESVendorShow, DeemingAlertList, LiabilityRecordList,
  DomainList, EntitlementListPage,
  PromotionList, PromotionShow, PromotionCreate, PromotionEdit,
  TenantList, TenantShow, TenantCreate, TenantEdit, TenantDashboard,
  } from '@/resources';

import {
  JournalEntryList, JournalEntryShow, JournalEntryCreate, JournalEntryEdit,
} from '@/ledger/journal-entry';
import { TransactionList } from '@/ledger/transaction/TransactionList';
import { TransactionShow } from '@/ledger/transaction/TransactionShow';
import { TransactionCreate } from '@/ledger/transaction/TransactionCreate';
import { TransactionEdit } from '@/ledger/transaction/TransactionEdit';
import {
  RefundList, RefundShow, RefundCreate, RefundEdit,
} from '@/ledger/refund';
import { LedgerDashboard } from '@/ledger/dashboard';

// Tutoring
import {
  TutorList, TutorShow, TutorCreate, TutorEdit,
  ClientList, ClientShow, ClientCreate, ClientEdit,
  StudentList, StudentShow, StudentCreate, StudentEdit,
  QualificationList, QualificationShow, QualificationCreate, QualificationEdit,
  QualificationTypeList, QualificationTypeDetail as QualificationTypeShow, QualificationTypeEdit as QualificationTypeCreate, QualificationTypeEdit,
  ServiceList, ServiceDetail as ServiceShow, ServiceEdit, ServiceCreate,
  ServiceAuthorisationList, ServiceAuthorisationShow, ServiceAuthorisationCreate, ServiceAuthorisationEdit,
  TutoringAgreementList, TutoringAgreementShow, TutoringAgreementCreate, TutoringAgreementEdit,
  TutoringScheduleList, TutoringScheduleShow, TutoringScheduleCreate, TutoringScheduleEdit,
  TutoringSessionList, TutoringSessionShow, TutoringSessionCreate, TutoringSessionEdit,
  AttendanceList, AttendanceShow, AttendanceCreate,
} from '@/modules/tutoring';

// Tutoring Wizards & Workflow Managers
import {
  TutorWizard,
  StudentWizard,
  ClientWizard,
  ServiceAuthorisationWizard,
  AttendanceWizard,
  TutoringSessionWizard,
  TutoringAgreementWizard,
  TutoringScheduleWizard,
  ServiceWizard,
  QualificationWizard,
  QualificationTypeWizard,
  TutoringSessionWorkflowManager,
  TutoringAgreementWorkflowManager,
  TutoringScheduleWorkflowManager,
  TutorWorkflowManager,
  StudentWorkflowManager,
  ClientWorkflowManager,
  TutoringDashboard,
} from '@/modules/tutoring';

import { CalendarView } from '@/resources/hr/ohs';
import { ExitInterview, TerminationDetail } from '@/resources/hr/offboarding';

import { OnboardingWizard } from '@/resources/hr/onboarding/wizards/OnboardingWizard';
import { DisciplinaryWizard } from '@/resources/hr/disciplinary/wizards/DisciplinaryWizard';
import { PipWizard } from '@/resources/hr/pip/wizards/PipWizard';
import { OffboardingWizard } from '@/resources/hr/offboarding/wizards/OffboardingWizard';
import { TrainingWizard } from '@/resources/hr/training/wizards/TrainingWizard';
import { WspAtrWizard } from '@/resources/hr/skills-development/wizards/WspAtrWizard';
import { InjuryWizard } from '@/resources/hr/ohs/wizards/InjuryWizard';
import { CertificationWizard } from '@/resources/hr/ohs/wizards/CertificationWizard';
import { CalendarEventWizard } from '@/resources/hr/ohs/wizards/CalendarEventWizard';
import { EEReportWizard } from '@/resources/hr/employment-equity/wizards/EEReportWizard';
import { AlertAcknowledgement } from '@/resources/hr/compliance/AlertAcknowledgement';
import { EntitlementGuard } from '@/components/domain-engine/EntitlementGuard';
import { IdentityVerificationList, IdentityVerificationShow } from '@/components/identity-verification/IdentityVerification';
import {
  ServiceCatalogueList,
  ServiceCatalogueShow,
  ServiceCatalogueCreate,
  ServiceCatalogueEdit,
} from '@/components/ledger/ServiceCatalogue';
import { ServiceOrderList, ServiceOrderShow } from '@/components/ledger/ServiceOrder';
import { OnboardingWorkflowManager } from '@/resources/hr/onboarding/WorkflowManager';
import { IndustrialRelationsWorkflowManager } from '@/resources/hr/industrial-relations/WorkflowManager';
import { OffboardingWorkflowManager } from '@/resources/hr/offboarding/WorkflowManager';
import { OHSWorkflowManager } from '@/resources/hr/ohs/WorkflowManager';
import { SkillsDevelopmentWorkflowManager } from '@/resources/hr/skills-development/WorkflowManager';
import { EmploymentEquityWorkflowManager } from '@/resources/hr/employment-equity/WorkflowManager';
import { ComplianceWorkflowManager } from '@/resources/hr/compliance/WorkflowManager';

const dataProvider = apiDataProvider;

function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('admin@rosta.co.za');
  const [password, setPassword] = useState('admin');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch {
      setError('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: '#050510',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Space background */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.2,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse 60% 50% at 70% 50%, rgba(26,76,216,0.3) 0%, transparent 60%),
            radial-gradient(ellipse 40% 40% at 30% 30%, rgba(201,177,7,0.15) 0%, transparent 50%)
          `,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(5,5,16,0.4) 0%, rgba(5,5,16,0.8) 100%)',
        }}
      />

      {/* Left panel — branding (hidden on mobile) */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'center',
          px: 8,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Stack spacing={4} sx={{ maxWidth: 480 }}>
          <Box component="img" src="/logo.svg" alt="Rosta" sx={{ height: 40, width: 'fit-content' }} />
          <Typography
            sx={{
              fontSize: 40,
              fontWeight: 800,
              lineHeight: 1.15,
              letterSpacing: '-0.03em',
              color: '#f0f0f5',
            }}
          >
            Modern HR for
            <br />
            South African
            <br />
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #3d7aff 0%, #c9b107 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Employers
            </Box>
          </Typography>
          <Typography sx={{ color: '#a0a5b5', fontSize: 17, lineHeight: 1.7 }}>
            Streamline onboarding, skills development, compliance, and industrial relations — all in one platform built for SA labour law.
          </Typography>
          <Stack spacing={2}>
            {['LRA, BCEA, EE Act, OHS compliant', 'POPIA-ready data protection', 'Real-time compliance dashboards'].map((text) => (
              <Stack key={text} direction="row" spacing={1} alignItems="center">
                <Box sx={{ color: '#22c55e', fontSize: 14, fontWeight: 700 }}>✓</Box>
                <Typography sx={{ color: '#a0a5b5', fontSize: 15 }}>{text}</Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Box>

      {/* Right panel — login form */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: { xs: '100%', md: '480px' },
          flexShrink: 0,
          position: 'relative',
          zIndex: 1,
          p: 4,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 6, textAlign: 'center' }}>
            <Box component="img" src="/logo.svg" alt="Rosta" sx={{ height: 32 }} />
          </Box>

          <Stack spacing={4}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f0f0f5', mb: 0.5 }}>
                Welcome back
              </Typography>
              <Typography sx={{ color: '#6b7a99', fontSize: 15 }}>
                Sign in to your Rosta account
              </Typography>
            </Box>

            {error && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)',
                }}
              >
                <Typography sx={{ color: '#ef4444', fontSize: 14 }}>{error}</Typography>
              </Box>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#f0f0f5',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                      '&.Mui-focused fieldset': { borderColor: '#3d7aff' },
                    },
                    '& .MuiInputLabel-root': { color: '#6b7a99' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#3d7aff' },
                  }}
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#f0f0f5',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                      '&.Mui-focused fieldset': { borderColor: '#3d7aff' },
                    },
                    '& .MuiInputLabel-root': { color: '#6b7a99' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#3d7aff' },
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: '9999px',
                    textTransform: 'none',
                    fontSize: 15,
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #1a4cd8, #3d7aff)',
                    '&:hover': { background: 'linear-gradient(135deg, #3d7aff, #1a4cd8)' },
                    '&:disabled': { background: 'rgba(61,122,255,0.3)' },
                  }}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </Stack>
            </Box>

            <Typography sx={{ color: '#6b7a99', fontSize: 14, textAlign: 'center' }}>
              Don't have an account?{' '}
              <Box
                component="a"
                href="/signup"
                sx={{ color: '#3d7aff', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Get started free
              </Box>
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

function AdminApp() {
  return (
    <Admin
      dataProvider={dataProvider}
      layout={RostaLayout}
      theme={theme}
      dashboard={Dashboard}
      basename="/"
    >
      <Resource name="employees" list={EmployeeList} show={EmployeeShow} create={EmployeeCreate} edit={EmployeeEdit} />
      <Resource name="training-records" list={TrainingList} show={TrainingShow} create={TrainingCreate} edit={TrainingEdit} />
      <Resource name="training-providers" list={TrainingProviderList} show={TrainingProviderShow} create={TrainingProviderCreate} edit={TrainingProviderEdit} />
      <Resource name="training-provider-promotions" list={PromotionList} show={PromotionShow} create={PromotionCreate} edit={PromotionEdit} />
      <Resource name="courses" list={CourseList} show={CourseShow} create={CourseCreate} edit={CourseEdit} />
      <Resource name="disciplinary-cases" list={DisciplinaryList} show={DisciplinaryShow} create={DisciplinaryCreate} edit={DisciplinaryEdit} />
      <Resource name="pips" list={PipList} show={PipShow} create={PipCreate} edit={PipEdit} />
      <Resource name="ee-profiles" list={EEProfileList} show={EEProfileShow} create={EEProfileCreate} edit={EEProfileEdit} />
      <Resource name="terminations" list={TerminationList} show={TerminationShow} create={TerminationCreate} edit={TerminationEdit} />
      <Resource name="invoices" list={InvoiceList} show={InvoiceShow} create={InvoiceCreate} edit={InvoiceEdit} />
      <Resource name="receipts" list={ReceiptList} show={ReceiptShow} create={ReceiptCreate} edit={ReceiptEdit} />
      <Resource name="refunds" list={RefundList} show={RefundShow} create={RefundCreate} edit={RefundEdit} />
      <Resource
        name="service-catalogues"
        list={ServiceCatalogueList}
        show={ServiceCatalogueShow}
        create={ServiceCatalogueCreate}
        edit={ServiceCatalogueEdit}
      />
      {/* M08 T2.4 — admin read surface; no create/edit (orders originate from BluePages). */}
      <Resource name="service-orders" list={ServiceOrderList} show={ServiceOrderShow} />
      <Resource name="ohs-certifications" list={CertificationList} show={CertificationShow} create={CertificationCreate} edit={CertificationEdit} />
      <Resource name="workplace-injuries" list={IncidentList} show={IncidentShow} create={IncidentCreate} edit={IncidentEdit} />
      <Resource name="ohs-representatives" list={RepresentativeList} show={RepresentativeShow} create={RepresentativeCreate} edit={RepresentativeEdit} />
      <Resource name="audit-logs" list={AuditList} show={AuditShow} create={AuditCreate} edit={AuditEdit} />
      <Resource name="compliance-calendar" list={ComplianceCalendarList} show={ComplianceCalendarShow} create={ComplianceCalendarCreate} edit={ComplianceCalendarEdit} />
      <Resource name="dsar-requests" list={DSARRequestList} show={DSARRequestShow} create={DSARRequestCreate} edit={DSARRequestEdit} />
      <Resource name="contracts" list={ContractList} show={ContractShow} create={ContractCreate} edit={ContractEdit} />
      <Resource name="onboarding-processes" list={OnboardingProcessList} show={OnboardingProcessShow} create={OnboardingProcessCreate} edit={OnboardingProcessEdit} />
      <Resource name="contract-alerts" list={ContractAlertList} show={ContractAlertShow} create={ContractAlertCreate} edit={ContractAlertEdit} />
      <Resource name="statutory-documents" list={StatutoryDocumentList} show={StatutoryDocumentShow} create={StatutoryDocumentCreate} edit={StatutoryDocumentEdit} />
      <Resource name="employment-contracts" list={EmploymentContractList} show={EmploymentContractShow} create={EmploymentContractCreate} edit={EmploymentContractEdit} />
      <Resource name="users" list={UserList} show={UserShow} create={UserCreate} edit={UserEdit} />
      <Resource name="roles" list={RoleList} show={RoleShow} create={RoleCreate} edit={RoleEdit} />
      <Resource name="notifications" list={NotificationList} show={NotificationShow} create={NotificationCreate} edit={NotificationEdit} />
      <Resource name="accounts" list={AccountList} show={AccountShow} create={AccountCreate} edit={AccountEdit} />
      <Resource name="journal-entries" list={JournalEntryList} show={JournalEntryShow} create={JournalEntryCreate} edit={JournalEntryEdit} />
      <Resource name="transactions" list={TransactionList} show={TransactionShow} create={TransactionCreate} edit={TransactionEdit} />
      <Resource name="account-checkpoints" list={CheckpointList} show={CheckpointShow} create={CheckpointCreate} />
      <Resource name="workflow-instances" list={WorkflowList} show={WorkflowShow} create={WorkflowCreate} edit={WorkflowEdit} />
      <Resource name="tasks" list={TaskList} show={TaskShow} />
      <Resource name="departments" list={DepartmentList} show={DepartmentShow} create={DepartmentCreate} edit={DepartmentEdit} />
      <Resource name="role-templates" list={RoleTemplateList} show={RoleTemplateShow} create={RoleTemplateCreate} edit={RoleTemplateEdit} />
      <Resource name="files" list={FileListShow} show={FileShowPage} create={FileCreatePage} edit={FileEditPage} />
      <Resource name="tenants" list={TenantList} show={TenantShow} create={TenantCreate} edit={TenantEdit} />
      <Resource name="domains" list={DomainList} />
      <Resource name="entitlements" list={EntitlementListPage} />
      <Resource name="identity-verifications" list={IdentityVerificationList} show={IdentityVerificationShow} />
      <Resource name="tes-assignments" list={TESAssignmentList} show={TESAssignmentShow} />
      <Resource name="tes-vendors" list={TESVendorList} show={TESVendorShow} />
      <Resource name="deeming-alerts" list={DeemingAlertList} />
      <Resource name="liability-records" list={LiabilityRecordList} />

      {/* Tutoring */}
      <Resource name="tutors" list={TutorList} show={TutorShow} create={TutorCreate} edit={TutorEdit} />
      <Resource name="clients" list={ClientList} show={ClientShow} create={ClientCreate} edit={ClientEdit} />
      <Resource name="students" list={StudentList} show={StudentShow} create={StudentCreate} edit={StudentEdit} />
      <Resource name="qualifications" list={QualificationList} show={QualificationShow} create={QualificationCreate} edit={QualificationEdit} />
      <Resource name="qualification-types" list={QualificationTypeList} show={QualificationTypeShow} create={QualificationTypeCreate} edit={QualificationTypeEdit} />
      <Resource name="services" list={ServiceList} show={ServiceShow} create={ServiceCreate} edit={ServiceEdit} />
      <Resource name="service-authorisations" list={ServiceAuthorisationList} show={ServiceAuthorisationShow} create={ServiceAuthorisationCreate} edit={ServiceAuthorisationEdit} />
      <Resource name="tutoring-agreements" list={TutoringAgreementList} show={TutoringAgreementShow} create={TutoringAgreementCreate} edit={TutoringAgreementEdit} />
      <Resource name="tutoring-schedules" list={TutoringScheduleList} show={TutoringScheduleShow} create={TutoringScheduleCreate} edit={TutoringScheduleEdit} />
      <Resource name="tutoring-sessions" list={TutoringSessionList} show={TutoringSessionShow} create={TutoringSessionCreate} edit={TutoringSessionEdit} />
      <Resource name="attendance" list={AttendanceList} show={AttendanceShow} create={AttendanceCreate} />
      <CustomRoutes>
        <Route path="/wizard/onboarding" element={<EntitlementGuard domainId="hr.onboarding"><OnboardingWizard /></EntitlementGuard>} />
        <Route path="/wizard/disciplinary" element={<EntitlementGuard domainId="hr.industrial-relations"><DisciplinaryWizard /></EntitlementGuard>} />
        <Route path="/wizard/pip" element={<EntitlementGuard domainId="hr.industrial-relations"><PipWizard /></EntitlementGuard>} />
        <Route path="/wizard/offboarding" element={<EntitlementGuard domainId="hr.offboarding"><OffboardingWizard /></EntitlementGuard>} />
        <Route path="/wizard/training" element={<EntitlementGuard domainId="hr.skills-development"><TrainingWizard /></EntitlementGuard>} />
        <Route path="/wizard/wsp-atr" element={<EntitlementGuard domainId="hr.skills-development"><WspAtrWizard /></EntitlementGuard>} />
        <Route path="/wizard/injury" element={<EntitlementGuard domainId="hr.ohs"><InjuryWizard /></EntitlementGuard>} />
        <Route path="/wizard/certification" element={<EntitlementGuard domainId="hr.ohs"><CertificationWizard /></EntitlementGuard>} />
        <Route path="/wizard/ohs-event" element={<EntitlementGuard domainId="hr.ohs"><CalendarEventWizard /></EntitlementGuard>} />
        <Route path="/wizard/ee-report" element={<EntitlementGuard domainId="hr.employment-equity"><EEReportWizard /></EntitlementGuard>} />
        <Route path="/wizard/contract-alert/:alertId" element={<EntitlementGuard domainId="hr.onboarding"><AlertAcknowledgement /></EntitlementGuard>} />
        <Route path="/wizard/tutor" element={<EntitlementGuard domainId="tutoring.tutor"><TutorWizard /></EntitlementGuard>} />
        <Route path="/wizard/student" element={<EntitlementGuard domainId="tutoring.student"><StudentWizard /></EntitlementGuard>} />
        <Route path="/wizard/client" element={<EntitlementGuard domainId="tutoring.client"><ClientWizard /></EntitlementGuard>} />
        <Route path="/wizard/service-authorisation" element={<EntitlementGuard domainId="tutoring.service-authorisation"><ServiceAuthorisationWizard /></EntitlementGuard>} />
        <Route path="/wizard/attendance" element={<EntitlementGuard domainId="tutoring.attendance"><AttendanceWizard /></EntitlementGuard>} />
        <Route path="/wizard/tutoring-session" element={<EntitlementGuard domainId="tutoring.session"><TutoringSessionWizard /></EntitlementGuard>} />
        <Route path="/wizard/tutoring-agreement" element={<EntitlementGuard domainId="tutoring.agreement"><TutoringAgreementWizard /></EntitlementGuard>} />
        <Route path="/wizard/tutoring-schedule" element={<EntitlementGuard domainId="tutoring.schedule"><TutoringScheduleWizard /></EntitlementGuard>} />
        <Route path="/wizard/service" element={<EntitlementGuard domainId="tutoring.service"><ServiceWizard /></EntitlementGuard>} />
        <Route path="/wizard/qualification" element={<EntitlementGuard domainId="tutoring.tutor"><QualificationWizard /></EntitlementGuard>} />
        <Route path="/wizard/qualification-type" element={<EntitlementGuard domainId="tutoring.qualification-type"><QualificationTypeWizard /></EntitlementGuard>} />
        <Route path="/wsp-atr-dashboard" element={<EntitlementGuard domainId="hr.skills-development"><WspAtrDashboard /></EntitlementGuard>} />
        <Route path="/skills-development/contracts" element={<ResourceContextProvider value="contracts"><ContractList /></ResourceContextProvider>} />
        <Route path="/skills-development/providers" element={<ResourceContextProvider value="training-providers"><TrainingProviderList /></ResourceContextProvider>} />
        <Route path="/skills-development/courses" element={<ResourceContextProvider value="courses"><CourseList /></ResourceContextProvider>} />
        <Route path="/onboarding-dashboard" element={<EntitlementGuard domainId="hr.onboarding"><OnboardingDashboard /></EntitlementGuard>} />
        <Route path="/ohs-calendar" element={<EntitlementGuard domainId="hr.ohs"><CalendarView /></EntitlementGuard>} />
        <Route path="/exit-interviews" element={<ExitInterview />} />
        <Route path="/termination-details" element={<TerminationDetail />} />
        <Route path="/user-dashboard" element={<UserDashboard />} />
        <Route path="/ledger-dashboard" element={<LedgerDashboard />} />
        <Route path="/tes-dashboard" element={<TESDashboard />} />
        <Route path="/tenant-dashboard" element={<TenantDashboard />} />
        <Route path="/hr/onboarding/workflows" element={<EntitlementGuard domainId="hr.onboarding"><OnboardingWorkflowManager /></EntitlementGuard>} />
        <Route path="/hr/industrial-relations/workflows" element={<EntitlementGuard domainId="hr.industrial-relations"><IndustrialRelationsWorkflowManager /></EntitlementGuard>} />
        <Route path="/hr/offboarding/workflows" element={<EntitlementGuard domainId="hr.offboarding"><OffboardingWorkflowManager /></EntitlementGuard>} />
        <Route path="/hr/ohs/workflows" element={<EntitlementGuard domainId="hr.ohs"><OHSWorkflowManager /></EntitlementGuard>} />
        <Route path="/hr/skills-development/workflows" element={<EntitlementGuard domainId="hr.skills-development"><SkillsDevelopmentWorkflowManager /></EntitlementGuard>} />
        <Route path="/hr/employment-equity/workflows" element={<EntitlementGuard domainId="hr.employment-equity"><EmploymentEquityWorkflowManager /></EntitlementGuard>} />
        <Route path="/hr/compliance/workflows" element={<EntitlementGuard domainId="hr.compliance"><ComplianceWorkflowManager /></EntitlementGuard>} />
        <Route path="/hr/tutoring/session/workflows" element={<EntitlementGuard domainId="tutoring.session"><TutoringSessionWorkflowManager /></EntitlementGuard>} />
        <Route path="/hr/tutoring/agreement/workflows" element={<EntitlementGuard domainId="tutoring.agreement"><TutoringAgreementWorkflowManager /></EntitlementGuard>} />
        <Route path="/hr/tutoring/schedule/workflows" element={<EntitlementGuard domainId="tutoring.schedule"><TutoringScheduleWorkflowManager /></EntitlementGuard>} />
        <Route path="/hr/tutoring/tutor/workflows" element={<EntitlementGuard domainId="tutoring.tutor"><TutorWorkflowManager /></EntitlementGuard>} />
        <Route path="/hr/tutoring/student/workflows" element={<EntitlementGuard domainId="tutoring.student"><StudentWorkflowManager /></EntitlementGuard>} />
        <Route path="/hr/tutoring/client/workflows" element={<EntitlementGuard domainId="tutoring.client"><ClientWorkflowManager /></EntitlementGuard>} />
        <Route path="/tutoring-dashboard" element={<EntitlementGuard domainId="tutoring"><TutoringDashboard /></EntitlementGuard>} />
        <Route path="/organization/departments" element={<ResourceContextProvider value="departments"><DepartmentList /></ResourceContextProvider>} />
        <Route path="/organization/role-templates" element={<ResourceContextProvider value="roletemplates"><RoleTemplateList /></ResourceContextProvider>} />
        <Route path="/organization/role-templates/:id/show" element={<ResourceContextProvider value="roletemplates"><RoleTemplateShow /></ResourceContextProvider>} />
        <Route path="/organization/role-templates/:id/edit" element={<ResourceContextProvider value="roletemplates"><RoleTemplateEdit /></ResourceContextProvider>} />
        <Route path="/tasks" element={<TaskList />} />
        <Route path="/tasks/new" element={<TaskShow />} />
        <Route path="/tasks/:id" element={<TaskShow />} />
      </CustomRoutes>
    </Admin>
  );
}

function AppContent() {
  const { isAuthenticated } = useAuth();
  return (
    <Routes>
      {/* Public routes — no auth required */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={isAuthenticated ? <Navigate to="/tutoring-sessions" replace /> : <LandingPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/sign-in" element={<LoginPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      </Route>

      {/* Everything else: admin if authenticated, redirect to sign-in if not */}
      <Route path="/*" element={
        isAuthenticated ? <AdminApp /> : <Navigate to="/sign-in" replace />
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}
